// Create Creative wizard (7.5).
//
// Intent first: what kind of creative, its name and its first format. The
// internal hierarchy (Client → Brand → Campaign) is created or reused BEHIND
// the flow for simple workspaces, and chosen explicitly only when the
// workspace actually has several clients/brands/campaigns ("Elegir
// ubicación"). Every call is an existing API operation, authorized by the
// server; the wizard only orchestrates them and can resume after a failure.
//
// No advertising platform is asked here (7.6F.2, ADR-0024): a Creative is
// platform-independent. WHERE it runs is chosen later, per Distribution
// (one creative may have several), when it is published.
//
// The first version starts empty and remembers the chosen format as an
// editor hint (`metadata.initialFormat` of the Creative Content v1 envelope):
// the Studio opens its canonical skeleton on that format; more formats are
// added in the Studio.

import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Check, Circle, LayoutTemplate, Loader2, Sparkles, X } from "lucide-react";
import { useRef, useState, type SyntheticEvent } from "react";
import { api, call } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { keys, useFrontDesk } from "@/api/hooks";
import type { CreativeProduct, FrontDesk } from "@/api/types";
import { COMMON_FORMATS, PRODUCTS, displayFormat } from "@/lib/labels";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { Field } from "@/ui/field";
import { Input, NativeSelect } from "@/ui/input";
import { Callout, PageHeader, Panel } from "@/ui/layout";
import { ContextPicker, type PickedContext } from "../common/context-picker";
import { pinnedRef, saveDocument, uploadAsset } from "../studio/api";
import { EMPTY_AUTHORING } from "../studio/core/model";
import {
  baseDocument,
  buildPrebuilt,
  type PickedAsset,
  type Prebuilt,
  type PrebuiltMedia,
  type PrebuiltValues,
} from "./prebuilt";
import { PrebuiltConfigurator, PrebuiltGallery, sizesOf, type LocalFiles } from "./prebuilt-gallery";
import { PageError, PageLoading, useTitle, useRouteParams, useSearchRecord } from "../common/page";

const DEFAULT_CAMPAIGN = "General";
const PRODUCT_ORDER: CreativeProduct[] = [
  "RICH_MEDIA_STANDARD",
  "VIDEO_RICH_MEDIA",
  "ADVERGAMING",
  "SURVEY_RICH_MEDIA",
];

type Placement =
  | { kind: "existing"; campaignId: string; label: string }
  | { kind: "new-campaign"; brandId: string; label: string }
  | { kind: "new-workspace-structure"; label: string }
  | { kind: "choose" }
  | { kind: "blocked"; reason: string };

/** How the creative will be placed, derived only from what the server reported. */
export function simplePlacement(fd: FrontDesk): Placement {
  const ctx = fd.creationContext;
  if (ctx?.campaignId) {
    const campaign = fd.campaigns.find((c) => c.id === ctx.campaignId);
    return { kind: "existing", campaignId: ctx.campaignId, label: campaign?.name ?? "tu campaña" };
  }
  if (ctx) {
    return fd.capabilities.manageCampaigns
      ? { kind: "new-campaign", brandId: ctx.brandId, label: `Campaña “${DEFAULT_CAMPAIGN}”` }
      : { kind: "blocked", reason: "Aún no hay una campaña. Pide a un administrador que cree una." };
  }
  if (fd.hierarchy.clients === 0) {
    return fd.capabilities.manageClients && fd.capabilities.manageCampaigns
      ? { kind: "new-workspace-structure", label: `${fd.organization.name} · ${DEFAULT_CAMPAIGN}` }
      : {
          kind: "blocked",
          reason: "Tu espacio de trabajo aún no está preparado. Pide a un administrador que lo configure.",
        };
  }
  return { kind: "choose" };
}

interface StepState {
  readonly label: string;
  status: "pending" | "running" | "done" | "error";
}

export function CreateCreativePage() {
  const { orgId } = useRouteParams("orgId");
  const search = useSearchRecord();
  const { data: fd, isLoading, error } = useFrontDesk(orgId);
  useTitle("Crear creativo");
  if (isLoading) return <PageLoading />;
  if (error || !fd) return <PageError error={error} orgId={orgId} />;
  if (!fd.capabilities.createCreative) {
    return (
      <Callout tone="warning" title="Sin permiso">
        Tu rol no permite crear creativos.
      </Callout>
    );
  }
  const campaignFromUrl = typeof search.campaignId === "string" ? search.campaignId : undefined;
  // ?start=vale|prebuilt (welcome of Creatividades): open that path directly.
  const start = search.start === "vale" || search.start === "prebuilt" ? search.start : undefined;
  return <Wizard orgId={orgId} fd={fd} presetCampaignId={campaignFromUrl} start={start} />;
}

function Wizard({
  orgId,
  fd,
  presetCampaignId,
  start,
}: {
  orgId: string;
  fd: FrontDesk;
  presetCampaignId: string | undefined;
  start?: "vale" | "prebuilt" | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const simple = simplePlacement(fd);
  // Two ways to start (report 109): VALE from scratch, or a prebuilt format configured without AI.
  const [path, setPath] = useState<"vale" | "prebuilt" | null>(start ?? null);
  const [prebuilt, setPrebuilt] = useState<Prebuilt | null>(null);
  const [values, setValues] = useState<PrebuiltValues>({});
  const [media, setMedia] = useState<PrebuiltMedia>({});
  const [localFiles, setLocalFiles] = useState<LocalFiles>(new Map());
  const [step, setStep] = useState<0 | 1 | 2 | 3 | 4>(start ? 1 : 0);
  const [product, setProduct] = useState<CreativeProduct | null>(null);
  const [name, setName] = useState("");
  const [format, setFormat] = useState<string>(COMMON_FORMATS[0]);
  const [customFormat, setCustomFormat] = useState("");
  const [advanced, setAdvanced] = useState(simple.kind === "choose" || presetCampaignId !== undefined);
  const [picked, setPicked] = useState<PickedContext>({});
  const [newCampaignName, setNewCampaignName] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepState[]>([]);
  const [running, setRunning] = useState(false);
  // Ids created so far: a retry resumes instead of duplicating.
  const created = useRef<{
    clientId?: string;
    brandId?: string;
    uploaded?: Map<string, PickedAsset>;
    campaignId?: string;
    creativeId?: string;
    versionId?: string;
  }>({});

  const effectiveFormat = format === "other" ? customFormat.trim() : format;

  const placementSummary = (): string => {
    if (presetCampaignId) return fd.campaigns.find((c) => c.id === presetCampaignId)?.name ?? "La campaña seleccionada";
    if (!advanced && simple.kind !== "choose" && simple.kind !== "blocked") return simple.label;
    if (picked.campaignId)
      return fd.campaigns.find((c) => c.id === picked.campaignId)?.name ?? "La campaña seleccionada";
    return newCampaignName.trim() ? `Nueva campaña “${newCampaignName.trim()}”` : "Sin elegir";
  };

  const validateDetails = (): boolean => {
    const errors: Record<string, string> = {};
    if (name.trim() === "") errors.name = "Ponle un nombre a tu creativo.";
    if (!/^\d{1,4}x\d{1,4}$/.test(effectiveFormat)) errors.format = "Indica un formato como 300x250 (ancho x alto).";
    if (advanced && !presetCampaignId) {
      if (!picked.brandId) errors.context = "Elige el cliente y la marca.";
      else if (!picked.campaignId && newCampaignName.trim() === "")
        errors.context = "Elige una campaña o escribe el nombre de una nueva.";
    }
    if (!advanced && simple.kind === "blocked") errors.context = simple.reason;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** The brand whose library the configuration offers (known once the location is). */
  const brandForMedia: string | null = presetCampaignId
    ? null
    : advanced
      ? (picked.brandId ?? null)
      : simple.kind === "existing" || simple.kind === "new-campaign"
        ? (fd.creationContext?.brandId ?? null)
        : null;
  const finalStep = path === "prebuilt" ? 4 : 3;
  const labels =
    path === "prebuilt"
      ? ["Inicio", "Formato", "Detalles", "Personalizar", "Crear"]
      : ["Inicio", "Tipo", "Detalles", "Crear"];

  const run = async () => {
    if (product === null) return;
    setRunning(true);
    setFailure(null);
    const plan: Array<{ label: string; exec: () => Promise<void> }> = [];
    const c = created.current;

    // ---- where the creative lives (created only when needed)
    if (presetCampaignId) {
      c.campaignId = presetCampaignId;
    } else if (advanced) {
      if (picked.campaignId) c.campaignId = picked.campaignId;
      else if (picked.brandId)
        plan.push({
          label: "Crear la campaña",
          exec: async () => {
            c.campaignId ??= (
              await call(() =>
                api.POST("/orgs/{orgId}/brands/{brandId}/campaigns", {
                  params: { path: { orgId, brandId: picked.brandId ?? "" } },
                  body: { name: newCampaignName.trim() },
                })
              )
            ).id;
          },
        });
    } else if (simple.kind === "existing") {
      c.campaignId = simple.campaignId;
    } else if (simple.kind === "new-campaign") {
      plan.push({
        label: "Preparar tu campaña",
        exec: async () => {
          c.campaignId ??= (
            await call(() =>
              api.POST("/orgs/{orgId}/brands/{brandId}/campaigns", {
                params: { path: { orgId, brandId: simple.brandId } },
                body: { name: DEFAULT_CAMPAIGN },
              })
            )
          ).id;
        },
      });
    } else if (simple.kind === "new-workspace-structure") {
      plan.push({
        label: "Preparar tu espacio de trabajo",
        exec: async () => {
          c.clientId ??= (
            await call(() =>
              api.POST("/orgs/{orgId}/clients", { params: { path: { orgId } }, body: { name: fd.organization.name } })
            )
          ).id;
          c.brandId ??= (
            await call(() =>
              api.POST("/orgs/{orgId}/clients/{clientId}/brands", {
                params: { path: { orgId, clientId: c.clientId ?? "" } },
                body: { name: fd.organization.name },
              })
            )
          ).id;
          c.campaignId ??= (
            await call(() =>
              api.POST("/orgs/{orgId}/brands/{brandId}/campaigns", {
                params: { path: { orgId, brandId: c.brandId ?? "" } },
                body: { name: DEFAULT_CAMPAIGN },
              })
            )
          ).id;
        },
      });
    }

    // ---- the creative and its first version (a draft on the chosen format). No platform: that is the Distribution.
    plan.push({
      label: "Crear el creativo",
      exec: async () => {
        c.creativeId ??= (
          await call(() =>
            api.POST("/orgs/{orgId}/campaigns/{campaignId}/creatives", {
              params: { path: { orgId, campaignId: c.campaignId ?? "" } },
              body: { name: name.trim(), product },
            })
          )
        ).id;
      },
    });
    plan.push({
      label: "Crear la primera versión",
      exec: async () => {
        c.versionId ??= (
          await call(() =>
            api.POST("/orgs/{orgId}/creatives/{creativeId}/versions", {
              params: { path: { orgId, creativeId: c.creativeId ?? "" } },
              body: { content: { components: [], metadata: { initialFormat: effectiveFormat } } },
            })
          )
        ).id;
      },
    });

    // ---- a prebuilt format: built here (no AI) and saved on the draft through the normal document path.
    const uploaded = (c.uploaded ??= new Map());
    const pendingFiles = [...localFiles].filter(([revisionId]) =>
      [media.image, media.logo, media.video, ...(media.pictures ?? [])].some((m) => m?.revisionId === revisionId)
    );
    if (path === "prebuilt" && pendingFiles.length > 0) {
      plan.push({
        label: "Subir tus archivos a la biblioteca de la marca",
        exec: async () => {
          const brandId =
            c.brandId ??
            (
              await call(() =>
                api.GET("/orgs/{orgId}/campaigns/{campaignId}", {
                  params: { path: { orgId, campaignId: c.campaignId ?? "" } },
                })
              )
            ).brandId;
          for (const [placeholder, file] of pendingFiles) {
            if (uploaded.has(placeholder)) continue;
            const asset = await uploadAsset(orgId, brandId, file);
            uploaded.set(placeholder, { ...pinnedRef(asset, "content"), name: asset.name });
          }
        },
      });
    }
    if (path === "prebuilt" && prebuilt) {
      plan.push({
        label: `Aplicar «${prebuilt.experience.name}»`,
        exec: async () => {
          const version = await call(() =>
            api.GET("/orgs/{orgId}/versions/{versionId}", { params: { path: { orgId, versionId: c.versionId ?? "" } } })
          );
          if ((version.content as { schemaVersion?: unknown }).schemaVersion === 2) return; // applied already
          const base = baseDocument(prebuilt.category, effectiveFormat, name.trim());
          if (!base) throw new Error("format");
          const real = (m: PickedAsset) => uploaded.get(m.revisionId) ?? m;
          const finalMedia: PrebuiltMedia = {
            ...(media.image ? { image: real(media.image) } : {}),
            ...(media.logo ? { logo: real(media.logo) } : {}),
            ...(media.video ? { video: real(media.video) } : {}),
            ...(media.pictures ? { pictures: media.pictures.map(real) } : {}),
          };
          const doc = buildPrebuilt(prebuilt, base, values, finalMedia);
          await saveDocument(orgId, c.versionId ?? "", doc, EMPTY_AUTHORING, version.draftRevision);
        },
      });
    }

    const state: StepState[] = plan.map((p) => ({ label: p.label, status: "pending" }));
    setSteps([...state]);
    for (const [i, p] of plan.entries()) {
      const current = state[i];
      if (!current) continue;
      current.status = "running";
      setSteps([...state]);
      try {
        await p.exec();
        current.status = "done";
        setSteps([...state]);
      } catch (problem) {
        current.status = "error";
        setSteps([...state]);
        setFailure(problem instanceof ApiProblem ? problem.message : "No se pudo completar este paso.");
        setRunning(false);
        return;
      }
    }
    await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    setRunning(false);
    await navigate({
      to: c.versionId ? `/o/${orgId}/versions/${c.versionId}/studio` : `/o/${orgId}/creatives/${c.creativeId ?? ""}`,
    });
  };

  const next = (event: SyntheticEvent) => {
    event.preventDefault();
    if (step === 0 && path !== null) setStep(1);
    else if (step === 1 && product !== null) setStep(2);
    else if (step === 2 && validateDetails()) setStep(3);
    else if (step === 3 && path === "prebuilt") setStep(4);
    else if (step === finalStep) void run();
  };
  const usePrebuilt = (p: Prebuilt) => {
    setPrebuilt(p);
    setProduct(p.category);
    setValues({});
    setMedia({});
    setLocalFiles(new Map());
    const sizes = sizesOf(p);
    if (!sizes.includes(format as (typeof sizes)[number])) setFormat(sizes[0] ?? COMMON_FORMATS[0]);
    if (name.trim() === "") setName(p.experience.name);
    setStep(2);
  };
  const formatChoices: readonly string[] = path === "prebuilt" && prebuilt ? sizesOf(prebuilt) : COMMON_FORMATS;
  const configured = (() => {
    if (path !== "prebuilt" || !prebuilt) return true;
    if (prebuilt.requires === "video" && !media.video) return false;
    return true;
  })();

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Crear creativo"
        description="Elige qué quieres crear; nosotros preparamos lo necesario para publicarlo."
      />
      <ol className="flex flex-wrap gap-2 text-xs" aria-label="Pasos">
        {labels.map((label, i) => (
          <li
            key={label}
            aria-current={step === i ? "step" : undefined}
            className={cn(
              "rounded-sm border px-2 py-1",
              step === i ? "border-brand bg-brand-soft font-medium text-brand" : "border-line text-muted"
            )}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      <form
        onSubmit={next}
        className={cn(
          "grid gap-6",
          step === 1 && path === "prebuilt"
            ? "max-w-6xl"
            : step === 3 && path === "prebuilt"
              ? "max-w-6xl"
              : "max-w-3xl"
        )}
        noValidate
      >
        {step === 0 ? (
          <fieldset className="grid gap-3">
            <legend className="mb-2 text-sm font-semibold">¿Cómo quieres empezar?</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  [
                    "vale",
                    "Crear con VALE",
                    "Describe tu anuncio y VALE lo diseña contigo desde cero. Usa los créditos de IA de tu organización.",
                    <Sparkles key="i" aria-hidden="true" />,
                  ],
                  [
                    "prebuilt",
                    "Usar un formato prediseñado",
                    "Elige un formato listo, personaliza textos, colores, imágenes y tiempos, y pruébalo. Sin programar y sin créditos de IA.",
                    <LayoutTemplate key="i" aria-hidden="true" />,
                  ],
                ] as const
              ).map(([id, title, description, icon]) => (
                <label
                  key={id}
                  className={cn(
                    "grid cursor-pointer gap-2 rounded-md border bg-surface p-4 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-focus",
                    path === id ? "border-brand ring-1 ring-brand" : "border-line hover:border-line-strong"
                  )}
                  data-testid={`start-${id}`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <input
                      type="radio"
                      name="path"
                      value={id}
                      checked={path === id}
                      onChange={() => {
                        setPath(id);
                      }}
                      aria-label={title}
                      className="accent-[var(--ia-brand)]"
                    />
                    {icon}
                    <span aria-hidden="true">{title}</span>
                  </span>
                  <span className="pl-5 text-sm text-muted">{description}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {step === 1 && path === "prebuilt" ? <PrebuiltGallery orgId={orgId} onUse={usePrebuilt} /> : null}

        {step === 3 && path === "prebuilt" && prebuilt ? (
          <PrebuiltConfigurator
            orgId={orgId}
            brandId={brandForMedia}
            p={prebuilt}
            format={effectiveFormat}
            name={name}
            values={values}
            media={media}
            local={localFiles}
            onValues={setValues}
            onMedia={setMedia}
            onLocal={setLocalFiles}
          />
        ) : null}

        {step === 1 && path === "vale" ? (
          <fieldset className="grid gap-3">
            <legend className="mb-2 text-sm font-semibold">¿Qué quieres crear?</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {PRODUCT_ORDER.map((p) => (
                <label
                  key={p}
                  className={cn(
                    "grid cursor-pointer gap-1 rounded-md border bg-surface p-3 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-focus",
                    product === p ? "border-brand ring-1 ring-brand" : "border-line hover:border-line-strong"
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="radio"
                      name="product"
                      value={p}
                      checked={product === p}
                      onChange={() => {
                        setProduct(p);
                      }}
                      aria-label={PRODUCTS[p].label}
                      aria-describedby={`product-${p}-description`}
                      className="accent-[var(--ia-brand)]"
                    />
                    <span aria-hidden="true">{PRODUCTS[p].label}</span>
                  </span>
                  <span id={`product-${p}-description`} className="pl-5 text-sm text-muted">
                    {PRODUCTS[p].description}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {step === 2 ? (
          <div className="grid gap-5">
            <Field label="Nombre del creativo" error={fieldErrors.name}>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                maxLength={200}
                autoFocus
                required
              />
            </Field>

            <fieldset className="grid gap-3">
              <legend className="mb-1 text-sm font-semibold">Formato inicial</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="Formato"
                  error={fieldErrors.format}
                  hint="Puedes añadir más formatos en el Studio. Dónde se usará lo eliges al publicar."
                >
                  <NativeSelect
                    value={format}
                    onChange={(e) => {
                      setFormat(e.target.value);
                    }}
                  >
                    {formatChoices.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                    {path === "prebuilt" ? null : <option value="other">Otro…</option>}
                  </NativeSelect>
                </Field>
                {format === "other" ? (
                  <Field label="Formato personalizado" hint="Ancho x alto, por ejemplo 300x250.">
                    <Input
                      value={customFormat}
                      onChange={(e) => {
                        setCustomFormat(e.target.value);
                      }}
                      maxLength={64}
                    />
                  </Field>
                ) : null}
              </div>
            </fieldset>

            <fieldset className="grid gap-3">
              <legend className="mb-1 text-sm font-semibold">Campaña</legend>
              {presetCampaignId ? (
                <p className="text-sm text-muted">
                  Se creará en la campaña desde la que llegaste; sus versiones permanecerán asociadas a ella.
                </p>
              ) : advanced ? (
                <>
                  <ContextPicker
                    orgId={orgId}
                    value={picked}
                    onChange={setPicked}
                    allowNewCampaign={fd.capabilities.manageCampaigns}
                  />
                  {picked.brandId && !picked.campaignId && fd.capabilities.manageCampaigns ? (
                    <Field label="Nombre de la nueva campaña">
                      <Input
                        value={newCampaignName}
                        onChange={(e) => {
                          setNewCampaignName(e.target.value);
                        }}
                        maxLength={200}
                      />
                    </Field>
                  ) : null}
                  {simple.kind !== "choose" ? (
                    <Button
                      variant="link"
                      className="justify-self-start"
                      onClick={() => {
                        setAdvanced(false);
                      }}
                    >
                      Usar la ubicación predeterminada
                    </Button>
                  ) : null}
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-muted">Se guardará en:</span>
                  <span className="font-medium">{simple.kind === "blocked" ? "—" : placementSummary()}</span>
                  {fd.hierarchy.clients > 0 ? (
                    <Button
                      variant="link"
                      onClick={() => {
                        setAdvanced(true);
                      }}
                    >
                      Elegir cliente, marca y campaña
                    </Button>
                  ) : null}
                </div>
              )}
              {fieldErrors.context ? (
                <p className="text-xs text-danger" role="alert">
                  {fieldErrors.context}
                </p>
              ) : null}
            </fieldset>
          </div>
        ) : null}

        {step === finalStep ? (
          <div className="grid gap-4">
            <Panel className="grid gap-2 p-4 text-sm">
              <p>
                <span className="text-muted">Tipo:</span> {product ? PRODUCTS[product].label : "—"}
              </p>
              {path === "prebuilt" && prebuilt ? (
                <p>
                  <span className="text-muted">Formato prediseñado:</span> {prebuilt.experience.name}
                </p>
              ) : null}
              <p>
                <span className="text-muted">Nombre:</span> {name.trim()}
              </p>
              <p>
                <span className="text-muted">Formato inicial:</span> {displayFormat(effectiveFormat)}
              </p>
              <p>
                <span className="text-muted">Ubicación:</span> {placementSummary()}
              </p>
            </Panel>
            {path === "prebuilt" ? (
              <Callout title="Tu anuncio ya funciona">
                Se crea como borrador con el formato que configuraste. En el Creative Studio puedes seguir ajustándolo a
                mano o pedirle cambios a VALE; luego lo envías a revisión y lo publicas.
              </Callout>
            ) : (
              <BuildStep />
            )}
            {steps.length > 0 ? (
              <ul className="grid gap-1 text-sm" aria-live="polite" aria-label="Progreso">
                {steps.map((s) => (
                  <li key={s.label} className="flex items-center gap-2">
                    {s.status === "done" ? (
                      <Check className="size-4 text-success" aria-label="Listo" />
                    ) : s.status === "running" ? (
                      <Loader2 className="size-4 animate-spin text-brand" aria-label="En curso" />
                    ) : s.status === "error" ? (
                      <X className="size-4 text-danger" aria-label="Error" />
                    ) : (
                      <Circle className="size-4 text-subtle" aria-label="Pendiente" />
                    )}
                    {s.label}
                  </li>
                ))}
              </ul>
            ) : null}
            {failure ? (
              <Callout tone="danger" title="No pudimos terminar">
                {failure} Puedes reintentar: continuaremos desde el último paso completado.
              </Callout>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {step > 0 ? (
            <Button
              variant="secondary"
              disabled={running}
              onClick={() => {
                setStep((s) => (s > 0 ? ((s - 1) as 0 | 1 | 2 | 3) : 0));
              }}
            >
              Atrás
            </Button>
          ) : (
            <Button asChild variant="secondary">
              <Link to={`/o/${orgId}`}>Cancelar</Link>
            </Button>
          )}
          {step === 1 && path === "prebuilt" ? null : (
            <Button
              type="submit"
              variant="primary"
              disabled={
                running ||
                (step === 0 && path === null) ||
                (step === 1 && product === null) ||
                (step === 3 && path === "prebuilt" && !configured)
              }
            >
              {step === finalStep ? (failure ? "Reintentar" : "Crear creativo") : "Continuar"}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

/** What happens next: the first version is a draft designed in the Creative Studio (7.6C+). */
function BuildStep() {
  return (
    <Callout title="Diseño del creativo">
      Tu creativo se crea como borrador. Después lo diseñas en el Creative Studio —textos, imágenes, botones,
      animaciones e interacciones—; cuando esté listo lo envías a revisión y, al publicarlo, eliges dónde vas a utilizar
      su Tag (StackAdapt, otra DSP o tu propia plataforma).
    </Callout>
  );
}
